import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const About: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="about" style={s.section} ref={ref}>

      {/* ── Topo: intro em largura total ── */}
      <motion.div
        style={s.intro}
        initial={{ opacity: 0, y: 24 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7 }}
      >
        <span style={s.label}>Sobre nós</span>
        <h2 style={s.title}>
          Construímos soluções tecnológicas<br />
          para os desafios de hoje e de amanhã.
        </h2>
      </motion.div>

      {/* ── Duas colunas de texto ── */}
      <motion.div
        style={s.textGrid}
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, delay: 0.15 }}
      >
        <p style={s.para}>
          A 2GF Innovation Systems é uma empresa portuguesa de consultoria tecnológica
          com sede no Centro de Inovação Empresarial da Covilhã. Trabalhamos com
          organizações industriais, agrícolas e municipais para implementar soluções
          digitais que geram impacto real e mensurável.
        </p>
        <p style={s.para}>
          A nossa abordagem assenta na integração de tecnologias como IoT, Big Data e
          automação de processos, adaptadas ao contexto e escala de cada cliente.
        </p>
      </motion.div>

      {/* ── Três pilares ── */}
      <div style={s.pillars}>
        {[
          {
            num: '01',
            area: 'Smart Industry',
            desc: 'Sensores, integração ERP, Big Data e sistemas de apoio à decisão para indústria transformadora.',
            img: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=800&q=80&auto=format&fit=crop',
          },
          {
            num: '02',
            area: 'Transformação Digital',
            desc: 'Engenharia de software, automação de processos e outsourcing de TI para empresas em crescimento.',
            img: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80&auto=format&fit=crop',
          },
          {
            num: '03',
            area: 'Smart Cities',
            desc: 'IoT urbano, espaços de dados verdes, agricultura de precisão e infraestrutura inteligente.',
            img: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=800&q=80&auto=format&fit=crop',
          },
        ].map((p, i) => (
          <motion.div
            key={i}
            style={s.pillar}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.65, delay: 0.25 + i * 0.12 }}
          >
            <div
              style={{
                ...s.pillarImg,
                backgroundImage: `url(${p.img})`,
              }}
            >
              <div style={s.pillarOverlay} />
              <span style={s.pillarNum}>{p.num}</span>
            </div>
            <div style={s.pillarBody}>
              <h3 style={s.pillarTitle}>{p.area}</h3>
              <p style={s.pillarDesc}>{p.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>


    </section>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  section: {
    background: '#ffffff',
    padding: '120px 0 0',
  },
  intro: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    marginBottom: '3rem',
  },
  label: {
    fontFamily: "'IBM Plex Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.25em',
    textTransform: 'uppercase',
    color: '#00C896',
    display: 'block',
    marginBottom: '1.25rem',
  },
  title: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)',
    fontWeight: 800,
    color: '#0D1B2A',
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
    maxWidth: '700px',
  },
  textGrid: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '3rem',
    marginBottom: '4rem',
  },
  para: {
    fontSize: '1rem',
    color: '#4A5568',
    lineHeight: 1.85,
    margin: 0,
  },
  pillars: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 2rem',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '1.5rem',
    marginBottom: '0',
  },
  pillar: {
    overflow: 'hidden',
    background: '#F7F9FC',
    borderTop: '1px solid #E8EDF3',
  },
  pillarImg: {
    height: '200px',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    padding: '1.25rem',
  },
  pillarOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to top, rgba(2,15,30,0.75) 0%, transparent 60%)',
  },
  pillarNum: {
    position: 'relative',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    color: 'rgba(255,255,255,0.5)',
  },
  pillarBody: {
    padding: '1.5rem',
  },
  pillarTitle: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0D1B2A',
    marginBottom: '0.6rem',
    letterSpacing: '-0.01em',
  },
  pillarDesc: {
    fontSize: '0.875rem',
    color: '#64748B',
    lineHeight: 1.7,
    margin: 0,
  },
  facts: {
    marginTop: '5rem',
    borderTop: '1px solid #E8EDF3',
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
  fact: {
    padding: '2.5rem 2rem',
    borderRight: '1px solid #E8EDF3',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  factVal: {
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    fontSize: '1.1rem',
    fontWeight: 800,
    color: '#0D1B2A',
    letterSpacing: '-0.01em',
  },
  factLabel: {
    fontSize: '0.8rem',
    color: '#94A3B8',
    fontWeight: 400,
    lineHeight: 1.4,
  },
};

export default About;
